/// <reference types="vite/client" />

import type { ReactNode } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { UserRole } from "@/constants";
import {
  ClientOnly,
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  redirect,
  Scripts,
} from "@tanstack/react-router";
import AppError from "@/features/shared/components/app-error";
import { UmamiAnalytics } from "@/features/shared/components/umami-analytics";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { kIsUnderMaintenance } from "@/constants";
import { type CurrentUser, userQueryOptions } from "@/lib/client/user";
import { getThemeIsomorphic } from "@/lib/utils/get-theme";
import {
  createStructuredDataScript,
  generateOrganizationStructuredData,
  generateWebsiteStructuredData,
  seo,
  SEO_SITE_URL,
} from "@/lib/utils/seo";
import appCss from "@/styles/global.css?url";
import { getPublicEnv, PublicEnvSchema } from "@/lib/env";
import {
  EARLY_ERUDA_BOOTSTRAP_SCRIPT,
  ErudaBootstrap,
  RootUserEffects,
  WechatJssdkBootstrap,
} from "@/features/shared/root-runtime-effects";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  authed: boolean;
  user: CurrentUser | null;
  roles: UserRole[];
  theme: "light" | "dark";
  env: PublicEnvSchema;
}>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=0.9, maximum-scale=0.9, user-scalable=no",
      },
      {
        name: "referrer",
        content: "strict-origin-when-cross-origin",
      },
      {
        name: "baidu-site-verification",
        content: "codeva-XmL87WxAyi",
      },
      ...seo({
        title: "CN Apex Club - 专业的 Apex Legends 游戏社区",
        description:
          "CN Apex Club 是专业的 Apex Legends 游戏社区，提供复盘服务、技巧分析、游戏工具和战术指导，帮助玩家提升游戏水平",
        keywords:
          "Apex Legends,CN Apex Club,猜圈,复盘,游戏技巧,战术分析,电竞,FPS,射击游戏,游戏社区,Apex社区",
        url: SEO_SITE_URL,
        type: "website",
      }),
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        href: "/favicon.png",
      },
      {
        rel: "apple-touch-icon",
        href: "/favicon.png",
      },
      {
        rel: "prefetch",
        href: "/game/apex-style",
      },
      {
        rel: "preload",
        href: "/fonts/noto-sans-sc-400.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: "/fonts/jetbrains-mono-400.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      createStructuredDataScript(generateWebsiteStructuredData()),
      createStructuredDataScript(generateOrganizationStructuredData()),
      { src: "https://res2.wx.qq.com/open/js/jweixin-1.6.0.js", defer: true },
    ],
  }),
  component: RootComponent,
  errorComponent: AppError,
  async beforeLoad(ctx) {
    if (kIsUnderMaintenance && ctx.location.pathname !== "/under-maintenance") {
      throw redirect({
        to: "/under-maintenance",
        statusCode: 302,
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
        replace: true,
      });
    }

    const env = getPublicEnv();
    const [user, theme] = await Promise.all([
      ctx.context.queryClient.ensureQueryData(userQueryOptions),
      getThemeIsomorphic(),
    ]);

    const roles = (user?.roles as UserRole[] | undefined) ?? [];

    return {
      theme,
      authed: user !== null,
      user,
      roles,
      env,
    };
  },
});

function RootComponent() {
  const { user } = Route.useRouteContext();

  return (
    <RootDocument>
      <RootUserEffects user={user} />
      <Outlet />
    </RootDocument>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  const { theme, env } = Route.useRouteContext();
  return (
    <html lang="zh-CN" className={theme}>
      <head>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: EARLY_ERUDA_BOOTSTRAP_SCRIPT }} />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.__INJECTED_ENV__ = ${JSON.stringify(env)}`,
          }}
        />
        <ClientOnly>
          <UmamiAnalytics />
        </ClientOnly>
      </head>

      <body className="font-sans">
        <WechatJssdkBootstrap />
        <ErudaBootstrap />
        <TooltipProvider>
          <div className="min-h-dvh relative z-10">{children}</div>
        </TooltipProvider>
        <Toaster richColors theme={theme} position="top-center" />
        <Scripts />
        {/* <img
          src="/algs-year5.png"
          className="z-0 fixed inset-0 size-full object-cover pointer-events-none"
          alt="ALGS Year 5 Background"
          draggable={false}
          loading="lazy"
          decoding="async"
        /> */}
      </body>
    </html>
  );
}
